/**
 * Magnétisme (snapping) au dessin/à l'édition de formes : logique pure en espace écran (pixels),
 * sans dépendance à maplibre-gl — testable sans DOM/carte. `MapLibreAdapter` se charge de projeter
 * les formes candidates en pixels (via `map.project()`) et d'appeler `resolveSnap`.
 *
 * Quatre paliers de priorité, chacun évalué seulement si le précédent n'a rien trouvé :
 *  1. accroche sur un sommet d'une autre forme (rayon serré) ;
 *  2. accroche sur une arête d'une autre forme (rayon un peu plus petit, pour que les sommets
 *     restent l'aimant le plus fort à la frontière où les deux seraient en portée) ;
 *  3. guide d'axe par rapport au dernier sommet posé sur le tracé EN COURS (`referencePoint`,
 *     fourni par l'appelant pendant `startDrawingPolygon`/`startDrawingLine` uniquement) : si le
 *     segment reliant ce sommet au curseur est proche de l'horizontale ou de la verticale écran
 *     (tolérance en DEGRÉS, pas en pixels — un segment court et un segment long doivent s'accrocher
 *     à la même tolérance angulaire, pas au même écart en pixels), verrouille l'axe concerné sur la
 *     coordonnée de ce sommet. Contrairement aux paliers 1/2, ceci ne verrouille jamais qu'UN seul
 *     axe (x OU y) — l'autre reste libre pour le palier suivant ;
 *  4. guides d'alignement (X et/ou Y) quand un sommet d'une autre forme partage (à peu près) la
 *     même coordonnée écran, même sans y toucher — accroche uniquement l'axe pas déjà verrouillé
 *     par le palier 3.
 * Un sommet/une arête accroché(e) (paliers 1/2) supprime tous les guides (déjà verrouillé sur les
 * deux axes).
 */

export type PixelPoint = { x: number; y: number };

export interface SnapEdge {
  a: PixelPoint;
  b: PixelPoint;
}

export interface SnapCandidates {
  vertices: PixelPoint[];
  edges: SnapEdge[];
}

export type SnapAxis = "x" | "y";

export interface AlignmentGuide {
  axis: SnapAxis;
  value: number;
  source: PixelPoint;
}

export interface SnapResult {
  point: PixelPoint;
  kind: "vertex" | "edge" | "none";
  guides: { x?: AlignmentGuide; y?: AlignmentGuide };
}

export interface SnapThresholds {
  vertexRadiusPx: number;
  edgeRadiusPx: number;
  alignmentTolerancePx: number;
  axisAngleToleranceDeg: number;
}

/**
 * Rayon sommet (10px) : accroche générique sur un sommet d'une AUTRE forme déjà finalisée (édition,
 * dessin) — mécanisme distinct de `CLOSE_CLICK_RADIUS_PX` (`MapLibreAdapter`), qui ne concerne que
 * le clic clôturant le polygone EN COURS de dessin sur son propre 1er sommet, jamais consommé par
 * `buildSnapCandidates`/`resolveSnap`. Rayon arête (8px) volontairement plus petit que le rayon
 * sommet. Tolérance d'alignement (8px) : un simple test perpendiculaire (pas de rayon le long de
 * l'axe), donc doit rester serrée pour ne pas accrocher un sommet sans rapport. Tolérance d'angle
 * (2°) : voir `resolveAxisGuide` — assez large pour rattraper un tracé approximatif à la souris sans
 * jamais snapper un segment délibérément en diagonale.
 */
export const DEFAULT_SNAP_THRESHOLDS: SnapThresholds = {
  vertexRadiusPx: 10,
  edgeRadiusPx: 8,
  alignmentTolerancePx: 8,
  axisAngleToleranceDeg: 2,
};

/** Sous ce seuil, l'angle entre `reference` et le curseur devient trop instable (division par une
 * distance quasi nulle) pour qu'un verrouillage d'axe ait un sens — voir `resolveAxisGuide`. */
const MIN_AXIS_GUIDE_DISTANCE_PX = 2;

/**
 * Guide d'axe (palier 3, voir le commentaire d'en-tête du fichier) : verrouille le segment
 * `reference` → `cursor` sur l'horizontale ou la verticale écran s'il en est à moins de
 * `toleranceDeg`. `null` si aucun des deux axes n'est assez proche, ou si `reference`/`cursor` sont
 * trop proches pour qu'un angle soit fiable.
 */
function resolveAxisGuide(cursor: PixelPoint, reference: PixelPoint, toleranceDeg: number): AlignmentGuide | null {
  const dx = cursor.x - reference.x;
  const dy = cursor.y - reference.y;
  if (Math.hypot(dx, dy) < MIN_AXIS_GUIDE_DISTANCE_PX) return null;

  // atan2 -> (-180, 180], ramené à [0, 180) : une droite a la même orientation à 180° près.
  const angleDeg = ((Math.atan2(dy, dx) * 180) / Math.PI + 360) % 180;
  const distanceToHorizontal = Math.min(angleDeg, 180 - angleDeg);
  const distanceToVertical = Math.abs(angleDeg - 90);

  if (distanceToHorizontal <= toleranceDeg && distanceToHorizontal <= distanceToVertical) {
    return { axis: "y", value: reference.y, source: reference };
  }
  if (distanceToVertical <= toleranceDeg) {
    return { axis: "x", value: reference.x, source: reference };
  }
  return null;
}

/** Point du segment [a,b] le plus proche de `p`, clampé aux extrémités au-delà du segment. */
export function nearestPointOnSegment(p: PixelPoint, a: PixelPoint, b: PixelPoint): PixelPoint {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const lengthSquared = abx * abx + aby * aby;
  if (lengthSquared === 0) return { x: a.x, y: a.y };

  const t = ((p.x - a.x) * abx + (p.y - a.y) * aby) / lengthSquared;
  const clampedT = Math.max(0, Math.min(1, t));
  return { x: a.x + clampedT * abx, y: a.y + clampedT * aby };
}

function distance(p: PixelPoint, q: PixelPoint): number {
  return Math.hypot(p.x - q.x, p.y - q.y);
}

/**
 * Construit les candidats de snapping (sommets dédoublonnés + arêtes) à partir d'un ring par forme
 * (en pixels écran). Le point de fermeture (dernier == premier) doit déjà avoir été retiré par
 * l'appelant — sinon il apparaîtrait deux fois côté sommets.
 */
export function buildSnapCandidates(shapesPointsPx: PixelPoint[][]): SnapCandidates {
  const vertices: PixelPoint[] = [];
  const edges: SnapEdge[] = [];

  for (const ring of shapesPointsPx) {
    for (const point of ring) vertices.push(point);
    for (let i = 0; i < ring.length; i++) {
      const a = ring[i]!;
      const b = ring[(i + 1) % ring.length]!;
      edges.push({ a, b });
    }
  }

  return { vertices, edges };
}

/**
 * Candidats tirés du tracé EN COURS (polygone ou ligne, sommets dans l'ordre de pose) : ses sommets
 * et les segments qui les relient, sans le dernier posé — le curseur en part, il s'y collerait — ni
 * le segment qui y mène. Pas d'arête de fermeture : le tracé n'est pas encore refermé.
 */
export function draftSnapCandidates(pointsPx: PixelPoint[]): SnapCandidates {
  const vertices = pointsPx.slice(0, -1);
  const edges: SnapEdge[] = [];
  for (let i = 0; i < vertices.length - 1; i++) edges.push({ a: vertices[i]!, b: vertices[i + 1]! });
  return { vertices, edges };
}

/** Réunit plusieurs jeux de candidats. */
export function mergeSnapCandidates(...sets: SnapCandidates[]): SnapCandidates {
  return { vertices: sets.flatMap((set) => set.vertices), edges: sets.flatMap((set) => set.edges) };
}

/**
 * Résout le point (potentiellement accroché) et les guides d'alignement pour un curseur donné.
 * `referencePoint` (dernier sommet posé sur le tracé polygone/ligne EN COURS, voir le commentaire
 * d'en-tête du fichier) est optionnel — omis ou `null`, le palier 3 ne s'applique simplement pas et
 * le comportement est inchangé par rapport à avant son introduction.
 */
export function resolveSnap(
  cursor: PixelPoint,
  candidates: SnapCandidates,
  thresholds: SnapThresholds = DEFAULT_SNAP_THRESHOLDS,
  referencePoint?: PixelPoint | null,
): SnapResult {
  let nearestVertex: PixelPoint | null = null;
  let nearestVertexDistance = Infinity;
  for (const vertex of candidates.vertices) {
    const d = distance(cursor, vertex);
    if (d < nearestVertexDistance) {
      nearestVertexDistance = d;
      nearestVertex = vertex;
    }
  }
  if (nearestVertex && nearestVertexDistance <= thresholds.vertexRadiusPx) {
    return { point: nearestVertex, kind: "vertex", guides: {} };
  }

  let nearestEdgePoint: PixelPoint | null = null;
  let nearestEdgeDistance = Infinity;
  for (const edge of candidates.edges) {
    const projected = nearestPointOnSegment(cursor, edge.a, edge.b);
    const d = distance(cursor, projected);
    if (d < nearestEdgeDistance) {
      nearestEdgeDistance = d;
      nearestEdgePoint = projected;
    }
  }
  if (nearestEdgePoint && nearestEdgeDistance <= thresholds.edgeRadiusPx) {
    return { point: nearestEdgePoint, kind: "edge", guides: {} };
  }

  const axisGuide = referencePoint ? resolveAxisGuide(cursor, referencePoint, thresholds.axisAngleToleranceDeg) : null;

  // Pré-rempli avec le guide d'axe (palier 3) sur son propre axe, à une distance de 0 : aucun
  // sommet d'une autre forme (palier 4, `dx`/`dy` toujours >= 0) ne peut faire mieux, donc la boucle
  // ci-dessous ne peut jamais l'écraser — seul l'AUTRE axe (resté `null`) reste disputable.
  let bestX: AlignmentGuide | null = axisGuide?.axis === "x" ? axisGuide : null;
  let bestXDistance = bestX ? 0 : Infinity;
  let bestY: AlignmentGuide | null = axisGuide?.axis === "y" ? axisGuide : null;
  let bestYDistance = bestY ? 0 : Infinity;
  for (const vertex of candidates.vertices) {
    const dx = Math.abs(cursor.x - vertex.x);
    if (dx <= thresholds.alignmentTolerancePx && dx < bestXDistance) {
      bestXDistance = dx;
      bestX = { axis: "x", value: vertex.x, source: vertex };
    }
    const dy = Math.abs(cursor.y - vertex.y);
    if (dy <= thresholds.alignmentTolerancePx && dy < bestYDistance) {
      bestYDistance = dy;
      bestY = { axis: "y", value: vertex.y, source: vertex };
    }
  }

  const point: PixelPoint = {
    x: bestX ? bestX.value : cursor.x,
    y: bestY ? bestY.value : cursor.y,
  };
  const guides: SnapResult["guides"] = {};
  if (bestX) guides.x = bestX;
  if (bestY) guides.y = bestY;

  return { point, kind: "none", guides };
}
